
import { openDB, IDBPDatabase } from 'idb';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

// Cache configuration
const CACHE_CONFIG = {
  chapterContent: {
    storeName: 'chapterContent',
    expirationTime: 24 * 60 * 60 * 1000, // 24 hours
  },
  images: {
    storeName: 'images',
    expirationTime: 7 * 24 * 60 * 60 * 1000, // 7 days
  }
};

class CacheManager {
  private db: IDBPDatabase | null = null;
  private dbName = 'pulseReaderCache';
  private version = 1;

  async initDB() {
    if (this.db) return this.db;

    this.db = await openDB(this.dbName, this.version, {
      upgrade(db) {
        // Create stores if they don't exist
        if (!db.objectStoreNames.contains(CACHE_CONFIG.chapterContent.storeName)) {
          db.createObjectStore(CACHE_CONFIG.chapterContent.storeName);
        }
        if (!db.objectStoreNames.contains(CACHE_CONFIG.images.storeName)) {
          db.createObjectStore(CACHE_CONFIG.images.storeName);
        }
      },
    });

    return this.db;
  }

  async set<T>(storeName: string, key: string, data: T, expirationTime: number): Promise<void> {
    const db = await this.initDB();
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + expirationTime,
    };
    await db.put(storeName, entry, key);
  }

  async get<T>(storeName: string, key: string): Promise<T | null> {
    try {
      const db = await this.initDB();
      const entry: CacheEntry<T> | undefined = await db.get(storeName, key);

      if (!entry) return null;

      // Check if entry has expired
      if (Date.now() > entry.expiresAt) {
        await this.delete(storeName, key);
        return null;
      }

      return entry.data;
    } catch (error) {
      console.error('Error reading from cache:', error);
      return null;
    }
  }

  async delete(storeName: string, key: string): Promise<void> {
    const db = await this.initDB();
    await db.delete(storeName, key);
  }

  async clear(storeName: string): Promise<void> {
    const db = await this.initDB();
    await db.clear(storeName);
  }
}

export const cacheManager = new CacheManager();
